import React, { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllFilterContacts,
  getAllTags,
} from "../../../../redux/Apps/Lawyerapp/ContactSlice/ContactSlice";
import { backend_api } from "../../../../static/server";
import {
  ListboxComponent,
  NoDataOptionField,
} from "../../../../static/staticdata";
import noOption from "../../../../assets/no-option-found.webp";
import { useDialog } from "../../../DialogContext";
import TagForm from "./TagForm";
import { AddRounded, KeyboardArrowDownRounded } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { toast } from "react-toastify";
import { pageScrollStyle } from "../../../../styles/styles";

const CompanyMiniForm = () => {
  const { showDialog, closeDialog } = useDialog();
  const dispatch = useDispatch();
  //tag option input field state
  const [tagOptions, setTagOptions] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagInputValue, setTagInputValue] = useState("");
  const [tagPage, setTagPage] = useState(1);
  const [tagHasMore, setTagHasMore] = useState(true);
  const [tagSearching, setTagSearching] = useState(false);
  const [isSubmiting, setIsSubmiting] = useState(false);
  const { token } = useSelector((data) => data.user);
  const { allTags, tagLoading } = useSelector((state) => state.contact);

  useEffect(() => {
    dispatch(
      getAllTags({
        url: `${backend_api}info-tags/`,
        token: token,
      })
    );
    loadTags(1, "");
  }, [dispatch, token]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      contact_type: "company",
      company_name: "",
      tags: [],
      reminder_mail: "",
      mobile: "",
      website_url: "",
      address_1: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
    },
  });
  const onSubmit = async (data) => {
    setIsSubmiting(true);
    try {
      const response = await axios.post(`${backend_api}contact/create/`, data, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      toast.success("Contact created successfully");
      dispatch(
        getAllFilterContacts({
          url: `${backend_api}contact/retrieve/?contact_type=company`,
          token,
        })
      );
      closeDialog();
      reset();
      return response;
    } catch (error) {
      toast.error("Error creating contact. Please try again later.");
      console.error("API Error:", error);
    } finally {
      setIsSubmiting(false);
      reset();
      reset({ tags: [] });
    }
  };

  const loadTags = useCallback(
    async (page = 1, search = "", reset = false) => {
      if (tagSearching) return;

      setTagSearching(true);
      try {
        const response = await dispatch(
          getAllTags({
            url: `${backend_api}info-tags/`,
            token: token,
            page: page,
            // pageSize: 20,
            search: search,
          })
        ).unwrap();

        if (reset || page === 1) {
          setTagOptions(response.results || []);
        } else {
          setTagOptions((prev) => [...prev, ...(response.results || [])]);
        }

        setTagHasMore(!!response.next);
      } catch (error) {
        console.error("Error loading tags:", error);
      } finally {
        setTagSearching(false);
      }
    },
    [dispatch, token, tagSearching]
  );

  const handleTagInputChange = useCallback(
    (newInputValue) => {
      setTagInputValue(newInputValue);
      setTagPage(1);

      // Debounce search
      const timeoutId = setTimeout(() => {
        loadTags(1, newInputValue, true);
      }, 300);

      return () => clearTimeout(timeoutId);
    },
    [loadTags]
  );
  const handleTagSelectionChange = (event, newValue, field) => {
    setSelectedTags(newValue);
    field.onChange(newValue.map((tag) => tag.id));
  };

  const handleScrollTagList = useCallback(
    (event) => {
      const { target } = event;
      if (target.scrollTop + target.clientHeight >= target.scrollHeight - 5) {
        if (tagHasMore && !tagSearching) {
          const nextPage = tagPage + 1;
          setTagPage(nextPage);
          loadTags(nextPage, tagInputValue);
        }
      }
    },
    [tagHasMore, tagSearching, tagPage, tagInputValue, loadTags]
  );
  return (
    <>
      <Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              flexDirection: "column",
              display: "flex",
              gap: "1rem",
              p: "1rem",
            }}
          >
            <Box>
              <Controller
                name="company_name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    label={
                      <span>
                        Company Name<span style={{ color: "red" }}>*</span>
                      </span>
                    }
                    size="small"
                    fullWidth
                    value={field.value || ""}
                    onChange={field.onChange}
                    error={!!errors.company_name}
                  />
                )}
              />
              {errors.company_name && (
                <Typography
                  variant="body1"
                  color="error"
                  sx={{ fontSize: "0.8rem" }}
                >
                  Company name is required
                </Typography>
              )}
            </Box>
            <Controller
              name="tags"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <FormControl fullWidth size="small">
                  <Autocomplete
                    multiple
                    options={tagOptions}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.name
                    }
                    value={selectedTags}
                    inputValue={tagInputValue}
                    onInputChange={(_, newInputValue) => {
                      handleTagInputChange(newInputValue);
                    }}
                    onChange={(_, newValue) =>
                      handleTagSelectionChange(_, newValue, field)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderTags={(tagValues, getTagProps) =>
                      tagValues?.map((option, index) => (
                        <Chip
                          size="small"
                          key={option.id}
                          label={option.name}
                          sx={{ bgcolor: option?.metadata?.color }}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tag"
                        placeholder="Select tags"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {tagSearching && (
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    noOptionsText={
                      <NoDataOptionField
                        imgSrc={noOption}
                        text={"Oops! No Tags to display."}
                        btnText={"Tags"}
                        handleClickNoData={() => {
                          showDialog(<TagForm />, {
                            dialogTitle: "Create Tag",
                          });
                        }}
                      />
                    }
                    ListboxComponent={(props) => (
                      <ListboxComponent
                        {...props}
                        btnText="Add tag"
                        btnIcon={<AddRounded />}
                        handleClikListBtn={() => {
                          showDialog(<TagForm />, {
                            dialogTitle: "Create Tag",
                          });
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: handleScrollTagList,
                      sx: pageScrollStyle,
                    }}
                  />
                </FormControl>
              )}
            />
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <Controller
                name="reminder_mail"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Email" size="small" fullWidth />
                )}
              />
              <Controller
                name="mobile"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Mobile No"
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Box>
            <Controller
              name="website_url"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Website URL"
                  size="small"
                  fullWidth
                />
              )}
            />
            <Accordion
              sx={{
                borderRadius: "0.6rem",
              }}
            >
              <AccordionSummary expandIcon={<KeyboardArrowDownRounded />}>
                <Typography
                  variant="body1"
                  sx={{ fontSize: "1rem", fontWeight: 500 }}
                >
                  Address Details
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                  }}
                >
                  <Box>
                    <Controller
                      name="address_1"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          multiline
                          rows={3}
                          fullWidth
                          label="Full Address"
                          placeholder="Enter the full address based on the verified ID."
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: "1rem" }}>
                    <Controller
                      name="city"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="City"
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                    <Controller
                      name="state"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="State"
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: "1rem" }}>
                    <Controller
                      name="pincode"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Pin code"
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                    <Controller
                      name="country"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Country"
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
          <Box
            sx={{
              height: "fit-content",
              borderTop: "1px solid #ddd",
              position: "sticky",
              bottom: 0,
              backdropFilter: "blur(10px)",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              p: "0.5rem",
              zIndex: 1000,
              gap: "0.5rem",
            }}
          >
            <Button variant="outlined" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <LoadingButton
              loading={isSubmiting}
              variant="contained"
              color="primary"
              type="submit"
              size="small"
            >
              Save Contact
            </LoadingButton>
          </Box>
        </form>
      </Box>
    </>
  );
};

export default CompanyMiniForm;
